import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOptionalDependency } from 'ditox-react';
import { useTranslation } from 'react-i18next';
import { WEB_SOCKET_MANAGER_TOKEN } from '@/container';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import useGroupInfo from '@/hooks/groups/use-group-info';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import type { RespondedType, ResponseError } from '@/models/response';
import { useMessageStore } from '@/stores/message';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';

export default function useQuitGroup(groupId: Nullable<number>, dismissible = false) {
  const { t } = useTranslation();
  const { isAdmin } = useGroupInfo(groupId);
  const webSocketManager = useOptionalDependency(WEB_SOCKET_MANAGER_TOKEN);
  const queryClient = useQueryClient();
  const { dispatch } = useCommDispatcher();

  return useMutation<RespondedType, ResponseError>({
    mutationKey: ['/QuitGroup', groupId],
    mutationFn: groupId
      ? () => fetcher(isAdmin ? '/DeleteGroup' : '/QuitGroup', { GroupID: groupId }, { validResponseCodes: [0] })
      : undefined,
    onSuccess: async () => {
      if (!groupId) return;

      if (dismissible) dispatch({ event: 'dismiss' });

      webSocketManager?.dispatch({
        action: 'unsubscribe',
        params: { groupId },
      });

      await Promise.allSettled([
        queryClient.invalidateQueries(groupsQueryOptions),
        queryClient.invalidateQueries(allChatSessionQueryOptions),
        useMessageStore.getState().deleteBySessionId('group', groupId),
      ]);
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: error ? error.message : isAdmin ? data?.Msg : t('conversation.group.has-left'),
      }),
  });
}
